import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { http } from '@/lib/http';

export interface SharedMailbox {
  id: number;
  name?: string;
  email?: string;
  is_default?: boolean;
  is_primary?: boolean;
}

export const sharedMailboxesQueryKey = ['settings-shared-mailboxes'] as const;

/** ~1.5 KB response, so a short window keeps pickers current cheaply. */
const STALE_TIME = 60 * 1000;

const fetchSharedMailboxes = async (
  signal?: AbortSignal
): Promise<SharedMailbox[]> => {
  const res = await http.get('/settings', { signal });
  const rows = (res as { data?: { user?: { shared_emails?: unknown } } })?.data
    ?.user?.shared_emails;
  return Array.isArray(rows) ? (rows as SharedMailbox[]) : [];
};

/**
 * Shared mailboxes the current user may send from.
 *
 * Read from React Query rather than the `bootstrap.settings` slice: that slice
 * is a login-time snapshot, so a mailbox assigned to someone's role after they
 * signed in stayed invisible until they refreshed (ENG-3768). The assignee is
 * usually not the admin who made the change, so invalidating on save alone
 * never reached them — the picker has to refetch in their own session.
 *
 * `/settings` resolves entitlements server-side, so the list is already scoped
 * to this user. Never source these from `/shared-emails`: that is the org-wide
 * admin list and is permission-gated.
 */
export function useSharedMailboxes() {
  const { data: sharedMailboxes = [], isLoading } = useQuery({
    queryKey: sharedMailboxesQueryKey,
    queryFn: ({ signal }) => fetchSharedMailboxes(signal),
    staleTime: STALE_TIME,
  });

  return { sharedMailboxes, isLoading };
}

/** Refresh the pickers in this tab right after a shared-email mutation. */
export function useInvalidateSharedMailboxes() {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: sharedMailboxesQueryKey }),
    [queryClient]
  );
}
